import { FC } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import Badge from '../../components/shared/Badge/Badge';
import BtButton from '../bt-button/bt-button';
import TagChips from '../page-supplier-evaluations/TagChips';
import { formatShortDay } from '../page-supplier-evaluations/reviewFormat';
import { OverviewRequestRow } from '../../services/service-evaluations';
import { EvaluationContextType, EvaluationRequestStatus } from '../../util/evaluationContract';
import { formatScore } from '../../util/evaluationFormat';
import { PROGRESS_BAR, requestStatusMeta } from '../../util/evaluationStatusMeta';
import { evaluationsUrl } from '../../util/evaluationsNavigation';
import styles from './app-evaluation-overview.module.scss';

type Props = {
  row: OverviewRequestRow;
  /** Per leverantör names the project the utvärdering belongs to. */
  contextLabel?: string;
};

const nameWithNumber = (name: string | null | undefined, number: string | null) =>
  name && number ? `${name} (${number})` : name || number || '';

export const requestContextLabel = (t: TFunction, row: OverviewRequestRow) => {
  if (row.contextType === EvaluationContextType.Project) {
    return nameWithNumber(row.projectName, row.projectNumber);
  }
  if (row.contextType === EvaluationContextType.Historical) {
    return t('overview.supplier.historical', '{{name}} (historiskt)', {
      name: nameWithNumber(row.historicalName ?? row.projectName, row.projectNumber),
    });
  }
  return t('overview.supplier.noProject', 'Inget projekt');
};

const RequestLine: FC<Props> = ({ row, contextLabel }) => {
  const [t, i18n] = useTranslation('evaluations');
  const history = useHistory();
  const location = useLocation();
  const meta = requestStatusMeta(row.status);
  const answeredShare = row.sentCount ? (row.answeredCount / row.sentCount) * 100 : 0;
  const overdue = row.status === EvaluationRequestStatus.Overdue;

  const open = () =>
    history.push(evaluationsUrl(`/requests/${row.requestId}`), {
      from: location.pathname + location.search,
    });

  return (
    <div className={styles.line}>
      <div className={styles.lineTitle}>
        <span className={styles.name} title={row.title}>
          {row.title}
        </span>
        {contextLabel && <span className={`${styles.small} ${styles.muted}`}>{contextLabel}</span>}
        <TagChips tags={row.tags} />
      </div>
      <div>
        <Badge color={meta.color} backgroundColor={meta.backgroundColor}>
          {t(meta.labelKey, meta.fallback)}
        </Badge>
      </div>
      <div className={styles.progress}>
        <div className={styles.progressTrack} style={{ background: PROGRESS_BAR.track }}>
          <div
            className={styles.progressFill}
            style={{ width: `${answeredShare}%`, background: PROGRESS_BAR.fill }}
          />
        </div>
        <span className={`${styles.small} ${styles.tabular}`}>
          {t('overview.line.answered', '{{answered}} av {{total}} svar', {
            answered: row.answeredCount,
            total: row.sentCount,
          })}
        </span>
      </div>
      <span className={`${styles.due} ${overdue ? styles.overdue : ''}`}>
        {t('overview.line.due', 'Sista svarsdag {{date}}', {
          date: formatShortDay(row.dueDate, i18n.language),
        })}
      </span>
      <span className={`${styles.tabular} ${styles.lineScore}`}>
        {row.score === null
          ? t('overview.line.noScore', 'Inget betyg')
          : t('overview.line.score', 'Betyg {{score}}', { score: formatScore(row.score) })}
      </span>
      <div className={styles.lineAction}>
        <BtButton color="white" size="xxs" tabIndex={0} onClick={open}>
          {t('overview.line.open', 'Öppna')}
        </BtButton>
      </div>
    </div>
  );
};

export default RequestLine;
